<script setup>
import { computed, onMounted, ref } from 'vue'
import BaseAdvancedFilter from '@/components/common/Filters/BaseAdvancedFilter.vue'
import BasePage from '@/components/Layouts/BasePage.vue'
import BaseQuickSearch from '@/components/common/Filters/BaseQuickSearch.vue'
import ButtonNew from '@/components/common/button/ButtonNew.vue'
import DialogConfirmDelet from '@/components/common/DialogConfirmDelet.vue'
import RHDigitalTeamCard from '@/components/rhdigital/teams/RHDigitalTeamCard.vue'
import SkeletonLoader from '@/components/common/SkeletonLoader.vue'
import BasePagination from '@/components/tables/BasePagination.vue'
import { RHDigitalTeamEntity } from '@/entities/RHDigitalTeamEntity.js'
import { getRHDigitalTeams } from '@/services/api/rhdigital.js'

const entity = new RHDigitalTeamEntity()

const items = ref([])
const totalItems = ref(0)
const loading = ref(false)
const currentPage = ref(1)
const itemsPerPage = ref(10)
const searchText = ref('')
const filters = ref({})
const expandedTeamId = ref({})
const showDialogConfirmDelet = ref(false)
const itemToDelete = ref(null)

const quickSearchConfig = computed(() => entity.pages.list.filters.quickSearch)

async function fetchData(params = {}) {
    loading.value = true

    try {
        currentPage.value = params.page ?? currentPage.value

        if (searchText.value) {
            const field = quickSearchConfig.value.searchBy[0]
            params[field] = `%${searchText.value}%`
        }

        if (filters.value && Object.keys(filters.value).length > 0) {
            params = {
                ...filters.value,
                ...params,
            }
        }

        const response = await getRHDigitalTeams({
            limit: itemsPerPage.value,
            page: currentPage.value,
            ...params,
        })

        items.value = response.data.items ?? response.data
        totalItems.value = response.page
    } finally {
        loading.value = false
    }
}

function onAdvancedFilter(filtersAdvanced) {
    filters.value = filtersAdvanced
    fetchData(filtersAdvanced)
}

function onCleanFilter() {
    filters.value = {}
    fetchData()
}

function toggleTeam(teamId) {
    expandedTeamId.value = expandedTeamId.value === teamId ? null : teamId
}

function onNoopAction(item) {
    itemToDelete.value = item
}

function onDelete(item) {
    itemToDelete.value = item
    showDialogConfirmDelet.value = true
}

function confirmDelete() {
    showDialogConfirmDelet.value = false
}

onMounted(fetchData)
</script>

<template>
    <BasePage :entity="entity">
        <v-row class="mb-4">
            <v-col>
                <ButtonNew
                    :route="entity.pages.create.route?.at(-1)"
                    :tooltip-text="$t('common.labels.availableSoon')"
                />
            </v-col>
            <v-col class="base-advanced-filter">
                <BaseAdvancedFilter
                    :entity="entity"
                    @filter="onAdvancedFilter"
                    @clean="onCleanFilter"
                />
            </v-col>
            <v-col class="base-quick-search">
                <BaseQuickSearch
                    :entity="entity"
                    :config="quickSearchConfig"
                    v-model="searchText"
                    :fetchData="fetchData"
                />
            </v-col>
        </v-row>

        <div class="rhdigital-teams-list">
            <SkeletonLoader v-if="loading" page="list" />

            <template v-else-if="items.length">
                <RHDigitalTeamCard
                    v-for="team in items"
                    :key="team.id"
                    :team="team"
                    :expanded="expandedTeamId === team.id"
                    :action-tooltip-text="$t('common.labels.availableSoon')"
                    @toggle="toggleTeam"
                    @view="onNoopAction"
                    @edit="onNoopAction"
                    @delete="onDelete"
                />
            </template>

            <div v-else class="rhdigital-teams-list__empty">
                {{ $t('common.labels.noData') }}
            </div>
        </div>

        <BasePagination
            :fetch-data="fetchData"
            :total-items="totalItems"
            :current-page="currentPage"
            :loading="loading"
            @page-change="(page) => fetchData({ page })"
        />

        <DialogConfirmDelet
            v-model="showDialogConfirmDelet"
            :item="itemToDelete?.nome"
            @confirm="confirmDelete"
        />
    </BasePage>
</template>

<style lang="scss" scoped>
.rhdigital-teams-list {
    display: flex;
    flex-direction: column;
    gap: 16px;
    min-height: 560px;

    &__empty {
        padding: 48px 20px;
        color: var(--bodyColor);
        text-align: center;
    }
}
</style>
